/**
 * EditRecurringOrderDialog Component
 *
 * Dialog for editing an existing recurring order's schedule and items.
 * Part of US-RECURRING-ORDERS (Step 8).
 *
 * Features:
 * - Edit days of week using RecurringOrderOptions
 * - Edit end conditions (Never, On date, After N occurrences)
 * - Add/remove/modify line items
 * - Regenerates all pending instances after save
 *
 * Changes apply to all FUTURE instances. Existing confirmed/skipped instances unchanged.
 */

import { useState, useEffect } from 'react'
import { toLocalDateString } from '../utils/dateUtils'
import { Plus, X, Loader2, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import RecurringOrderOptions from './RecurringOrderOptions'
import { useDataCache } from '@/contexts/DataCacheContext'
import {
  updateRecurringOrder,
  updateRecurringOrderItems,
  regenerateInstances,
  updateInstanceItems,
  cancelRecurringOrder
} from '../services/recurringOrderService'
import { fetchCustomerBranches } from '../supabase-client'

/**
 * ItemsEditor - Inline component for editing recurring order line items
 *
 * @param {Object} props
 * @param {Array} props.items - Current items array
 * @param {Function} props.onChange - Callback when items change
 * @param {Array} props.products - Available products
 * @param {Array} props.productTypes - Available product types
 * @param {boolean} props.disabled - Whether inputs are disabled
 */
function ItemsEditor({ items, onChange, products, productTypes, disabled }) {
  /**
   * Add a new empty item
   */
  const addItem = () => {
    onChange([
      ...items,
      {
        product_id: '',
        product_type_id: '',
        quantity: 1
      }
    ])
  }

  /**
   * Remove item at index
   */
  const removeItem = (index) => {
    onChange(items.filter((_, i) => i !== index))
  }

  /**
   * Update a field on an item
   */
  const updateItem = (index, field, value) => {
    const updated = [...items]
    updated[index] = { ...updated[index], [field]: value }
    onChange(updated)
  }

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Products</Label>

      {items.length === 0 && (
        <p className="text-sm text-muted-foreground">No products added yet</p>
      )}

      {items.map((item, index) => (
        <div key={index} className="flex gap-2 items-center">
          {/* Product Select */}
          <Select
            value={item.product_id}
            onValueChange={(v) => updateItem(index, 'product_id', v)}
            disabled={disabled}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.strain}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Product Type Select */}
          <Select
            value={item.product_type_id}
            onValueChange={(v) => updateItem(index, 'product_type_id', v)}
            disabled={disabled}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              {productTypes.map((t) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Quantity Input */}
          <div className="flex items-center gap-1">
            <Input
              type="number"
              min="0.1"
              step="0.1"
              value={item.quantity}
              onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
              disabled={disabled}
              className="w-20"
            />
            <span className="text-sm text-gray-500">kg</span>
          </div>

          {/* Remove Button */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => removeItem(index)}
            disabled={disabled}
            className="p-2 h-8 w-8"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ))}

      {/* Add Item Button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addItem}
        disabled={disabled}
        className="gap-1"
      >
        <Plus className="w-4 h-4" />
        Add Product
      </Button>
    </div>
  )
}

/**
 * EditRecurringOrderDialog - Main dialog component
 *
 * @param {Object} props
 * @param {Object} props.order - The recurring order to edit
 * @param {string} props.instanceId - The specific instance ID (for "this order only" saves)
 * @param {string} props.customerId - Customer ID for fetching branches
 * @param {boolean} props.open - Whether dialog is open
 * @param {Function} props.onOpenChange - Callback when open state changes
 * @param {Function} props.onSave - Callback after successful save
 */
export default function EditRecurringOrderDialog({
  order,
  instanceId,
  customerId,
  open,
  onOpenChange,
  onSave
}) {
  // Schedule state
  const [days, setDays] = useState([])
  const [endType, setEndType] = useState('indefinite')
  const [endDate, setEndDate] = useState(null)
  const [occurrences, setOccurrences] = useState(null)

  // Branch state
  const [branches, setBranches] = useState([])
  const [selectedBranchId, setSelectedBranchId] = useState('')

  // Items state
  const [items, setItems] = useState([])

  // Products and product types for dropdowns
  const [products, setProducts] = useState([])
  const [productTypes, setProductTypes] = useState([])
  const [loadingData, setLoadingData] = useState(true)

  // Save state
  const [saving, setSaving] = useState(false)
  const [showSaveScope, setShowSaveScope] = useState(false)

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Data cache for products and product types
  const { getProducts, getProductTypes } = useDataCache()

  /**
   * Initialize state from order when dialog opens
   */
  useEffect(() => {
    if (open && order) {
      // Set schedule state
      setDays(order.days_of_week || [])
      setEndType(order.end_type || 'indefinite')
      setEndDate(order.end_date ? new Date(order.end_date) : null)
      setOccurrences(order.max_occurrences || null)

      // Set branch state
      setSelectedBranchId(order.branch_id || '')

      // Set items state - transform from order.items format
      const transformedItems = (order.items || []).map(item => ({
        product_id: item.product_id,
        product_type_id: item.product_type_id,
        quantity: item.quantity
      }))
      setItems(transformedItems)

      // Fetch products, product types, and branches
      setLoadingData(true)
      const fetchPromises = [getProducts(), getProductTypes()]

      // Only fetch branches if customerId is provided
      if (customerId) {
        fetchPromises.push(fetchCustomerBranches(customerId))
      }

      Promise.all(fetchPromises)
        .then(([productsData, productTypesData, branchesData]) => {
          setProducts(productsData || [])
          setProductTypes(productTypesData || [])
          setBranches(branchesData || [])
        })
        .catch((err) => {
          console.error('[EditRecurringOrderDialog] Error loading data:', err)
          toast.error('Failed to load data')
        })
        .finally(() => {
          setLoadingData(false)
        })
    }
  }, [open, order, customerId, getProducts, getProductTypes])

  /**
   * Validate form before saving
   */
  const validateForm = () => {
    // Require branch selection if customer has multiple branches
    if (branches.length > 1 && !selectedBranchId) {
      toast.error('Please select a branch')
      return false
    }

    if (days.length === 0) {
      toast.error('Please select at least one day')
      return false
    }

    if (items.length === 0) {
      toast.error('Please add at least one product')
      return false
    }

    // Check each item has required fields
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (!item.product_id) {
        toast.error(`Please select a product for item ${i + 1}`)
        return false
      }
      if (!item.product_type_id) {
        toast.error(`Please select a product type for item ${i + 1}`)
        return false
      }
      if (!item.quantity || item.quantity <= 0) {
        toast.error(`Please enter a valid quantity for item ${i + 1}`)
        return false
      }
    }

    // Validate end date if type is until_date
    if (endType === 'until_date' && !endDate) {
      toast.error('Please select an end date')
      return false
    }

    // Validate occurrences if type is after_count
    if (endType === 'after_count' && (!occurrences || occurrences < 1)) {
      toast.error('Please enter a valid number of occurrences')
      return false
    }

    return true
  }

  /**
   * Handle save button click - shows confirmation dialog
   */
  const handleSaveClick = () => {
    if (!validateForm()) return
    setShowSaveScope(true)
  }

  /**
   * Save changes to this instance only
   */
  const handleSaveThisOnly = async () => {
    setShowSaveScope(false)
    setSaving(true)

    try {
      // Update just this instance's modified_items
      const itemsForInstance = items.map(item => ({
        productId: item.product_id,
        productTypeId: item.product_type_id,
        quantity: item.quantity
      }))

      await updateInstanceItems(instanceId, itemsForInstance)

      toast.success('Changes saved for this order only')
      onSave()
      onOpenChange(false)
    } catch (err) {
      console.error('[EditRecurringOrderDialog] Error saving instance:', err)
      toast.error('Failed to save changes', {
        description: err.message || 'Please try again'
      })
    } finally {
      setSaving(false)
    }
  }

  /**
   * Save changes to all future orders
   */
  const handleSaveAllFuture = async () => {
    setShowSaveScope(false)
    setSaving(true)

    try {
      // 1. Update recurring order pattern (including branch)
      const updatePayload = {
        daysOfWeek: days,
        endType: endType,
        endDate: endType === 'until_date' ? toLocalDateString(endDate) : null,
        maxOccurrences: endType === 'after_count' ? occurrences : null,
        branchId: selectedBranchId || null
      }
      await updateRecurringOrder(order.id, updatePayload)

      // 2. Update items (replace all)
      const itemsForService = items.map(item => ({
        productId: item.product_id,
        productTypeId: item.product_type_id,
        quantity: item.quantity
      }))
      await updateRecurringOrderItems(order.id, itemsForService)

      // 3. Regenerate instances for the new pattern
      await regenerateInstances(order.id)

      toast.success('Changes saved for all future orders')
      onSave()
      onOpenChange(false)
    } catch (err) {
      console.error('[EditRecurringOrderDialog] Error saving:', err)
      toast.error('Failed to save changes', {
        description: err.message || 'Please try again'
      })
    } finally {
      setSaving(false)
    }
  }

  /**
   * Handle delete - soft-deletes the recurring order and removes pending instances
   */
  const handleDelete = async () => {
    setDeleting(true)
    try {
      await cancelRecurringOrder(order.id)
      toast.success('Order deleted', {
        description: 'All future deliveries have been stopped'
      })
      setShowDeleteConfirm(false)
      onSave()
      onOpenChange(false)
    } catch (err) {
      console.error('[EditRecurringOrderDialog] Error deleting order:', err)
      toast.error('Failed to delete order', {
        description: err.message || 'Please try again'
      })
    } finally {
      setDeleting(false)
    }
  }

  /**
   * Handle cancel/close
   */
  const handleClose = () => {
    if (!saving && !deleting) {
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Recurring Order</DialogTitle>
          <DialogDescription>
            Changes will apply to all future deliveries. Existing confirmed or skipped deliveries will not be affected.
          </DialogDescription>
        </DialogHeader>

        {loadingData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-500">Loading...</span>
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Branch Selector - show if customer has branches */}
            {branches.length > 0 && (
              <div className="space-y-1">
                <Label htmlFor="branch-select" className="text-sm font-medium">
                  Branch {branches.length > 1 && <span className="text-destructive">*</span>}
                </Label>
                <Select
                  value={selectedBranchId}
                  onValueChange={setSelectedBranchId}
                  disabled={saving}
                >
                  <SelectTrigger id="branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Schedule Options - Reusing RecurringOrderOptions */}
            <RecurringOrderOptions
              selectedDays={days}
              onDaysChange={setDays}
              endType={endType}
              onEndTypeChange={setEndType}
              endDate={endDate}
              onEndDateChange={setEndDate}
              occurrences={occurrences}
              onOccurrencesChange={setOccurrences}
            />

            {/* Items Editor */}
            <div className="border-t pt-4">
              <ItemsEditor
                items={items}
                onChange={setItems}
                products={products}
                productTypes={productTypes}
                disabled={saving}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          {showDeleteConfirm ? (
            <div className="w-full space-y-3">
              <div className="text-sm font-medium text-destructive">
                Delete this recurring order? This will permanently stop all future deliveries and remove all pending instances.
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Keep Order
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Delete Order'
                  )}
                </Button>
              </div>
            </div>
          ) : !showSaveScope ? (
            <div className="flex w-full justify-between">
              <Button
                variant="ghost"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving || deleting}
                className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-1"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveClick}
                  disabled={saving || loadingData}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="w-full space-y-3">
              <div className="text-sm text-muted-foreground">
                Apply changes to which orders?
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
                <Button
                  variant="outline"
                  onClick={() => setShowSaveScope(false)}
                  disabled={saving}
                >
                  Back
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleSaveThisOnly}
                    disabled={saving}
                  >
                    Just This Order
                  </Button>
                  <Button
                    onClick={handleSaveAllFuture}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'All Future Orders'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
